import {useState, useEffect, useCallback, useMemo} from "react";
import {useQuery, useQueryClient, useMutation} from "@tanstack/react-query";
import type {AutoSaveStatus} from "./DocumentCreator";
import {AttachmentPanel} from "./AttachmentPanel";
import {RightSidebarAttachmentInner} from "./RightSidebarAttachmentInner";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {useLibrary} from "@/contexts/LibraryContext";
import {useAuth} from "@/contexts/AuthContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {useMastermind} from "@/contexts/MastermindContext";
import type {MastermindSpace} from "@shared/schema";
import {useLocation} from "wouter";
import {foldersService} from "@/services/foldersService";
import {knowledgeBaseService} from "@/services/knowledgeBaseService";
import {useMergedKnowledgeDocuments} from "@/hooks/useMergedKnowledgeDocuments";
import {useToast} from "@/hooks/use-toast";
import {ragService} from "@/services/ragService";
import {sourcesService} from "@/services/sourcesService";
import type {DocumentActionsItem} from "@/components/DocumentActionsMenuContent";
import type {LibraryDocumentActions} from "@/components/LibraryDocumentRow";
import { SOURCE_OF_TRUTH_QUERY_KEY } from "@/hooks/useSourceOfTruth";
import { toggleDocumentSourceOfTruth } from "@/services/sourceOfTruthService";
import { useCopyPublicDocumentLink } from "@/hooks/useCopyPublicDocumentLink";

interface RightSidebarProps {
    onItemClick?: (itemId: string, itemType: "document" | "source") => void;
}

function resolveLinkedSpaceIdForSharedEdit(
    doc: {userId?: string; metadata?: unknown; spaceId?: string | null} | undefined,
    viewerUserId: string | undefined
): string | null {
    if (!doc || !viewerUserId || doc.userId === viewerUserId) {
        return null;
    }
    let metadata: unknown = doc.metadata ?? {};
    if (typeof metadata === "string") {
        try {
            metadata = JSON.parse(metadata);
        } catch {
            metadata = {};
        }
    }
    const meta = metadata as {spaceIds?: string[]; spaceId?: string};
    const spaceIds = Array.isArray(meta.spaceIds)
        ? meta.spaceIds
        : meta.spaceId
          ? [meta.spaceId]
          : doc.spaceId
            ? [doc.spaceId]
            : [];
    return spaceIds.length > 0 ? String(spaceIds[0]) : null;
}

export function RightSidebar({onItemClick: _onItemClick}: RightSidebarProps) {
    const [, navigate] = useLocation();
    const {user} = useAuth();
    const {currentWorkspaceId} = useWorkspace();
    const {setIsCollapsed} = useMastermind();
    const {
        selectedFolderId,
        setSelectedFolderId,
        expandedFolderIds,
        toggleFolderExpanded,
        selectedDocumentId,
        setSelectedDocumentId,
        setDocumentEditSpaceContextId,
    } = useLibrary();
    const [activeTab, setActiveTab] = useState("create");
    const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle");
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [openDocumentHandler, setOpenDocumentHandler] = useState<((doc: any) => Promise<void>) | null>(null);
    const [pendingDocumentIdToOpen, setPendingDocumentIdToOpen] = useState<string | null>(null);
    const [showPinDialogForDocument, setShowPinDialogForDocument] = useState(false);
    const queryClient = useQueryClient();
    const {toast} = useToast();
    const { copyPublicLink } = useCopyPublicDocumentLink();

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renamingItem, setRenamingItem] = useState<DocumentActionsItem | null>(null);
    const [newTitle, setNewTitle] = useState("");
    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
    const [mergeSourceItem, setMergeSourceItem] = useState<DocumentActionsItem | null>(null);
    const [mergeTargetId, setMergeTargetId] = useState("");
    const [moveToFolderDialogOpen, setMoveToFolderDialogOpen] = useState(false);
    const [moveFolderItem, setMoveFolderItem] = useState<DocumentActionsItem | null>(null);
    const [moveFolderTargetId, setMoveFolderTargetId] = useState<string | null>(null);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const [inspectorItem, setInspectorItem] = useState<DocumentActionsItem | null>(null);
    const [pendingDocumentSwitch, setPendingDocumentSwitch] = useState<string | null>(null);

    const handleActivateCreateTab = useCallback(() => {
        setActiveTab("create");
    }, []);

    // When a document is selected (e.g. after creating from chat "Save as Document"), switch to Create/Editing tab
    // But wait for current document to finish saving if it's in progress
    useEffect(() => {
        if (selectedDocumentId) {
            // Check if we're currently saving a document
            if (autoSaveStatus === "saving" && editingItemId !== selectedDocumentId) {
                // Store the pending document ID to switch to after save completes
                setPendingDocumentSwitch(selectedDocumentId);
                // Clear selectedDocumentId to prevent immediate switch
                setSelectedDocumentId(null);
                // Show user-friendly message
                toast({
                    title: "Saving in progress",
                    description: "Please wait while the current document finishes saving...",
                    duration: 3000,
                });
                return;
            }
            // If save is complete or no document is being edited, switch immediately
            setActiveTab("create");
        }
    }, [selectedDocumentId, autoSaveStatus, editingItemId, setSelectedDocumentId, toast]);

    // Watch for save completion and switch to pending document
    useEffect(() => {
        if (pendingDocumentSwitch && autoSaveStatus !== "saving") {
            // Save has completed (status is "idle" or "saved"), now switch to the pending document
            const pendingId = pendingDocumentSwitch;
            setPendingDocumentSwitch(null);
            // Small delay to ensure save is fully complete and state has settled
            setTimeout(() => {
                setSelectedDocumentId(pendingId);
                setActiveTab("create");
                toast({
                    title: "Document saved",
                    description: "Switching to the new document...",
                    duration: 2000,
                });
            }, 300);
        }
    }, [pendingDocumentSwitch, autoSaveStatus, setSelectedDocumentId, toast]);

    // Fix for hidden tab panels taking space
    useEffect(() => {
        const styleId = 'right-sidebar-tab-fix';
        let style = document.getElementById(styleId) as HTMLStyleElement;
        if (!style) {
            style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
        [data-state="inactive"][role="tabpanel"] {
          display: none !important;
          height: 0 !important;
          min-height: 0 !important;
          flex: none !important;
          overflow: hidden !important;
        }
      `;
            document.head.appendChild(style);
        }
        return () => {
            // Don't remove on unmount as other instances might need it
        };
    }, []);
    const [docSearchQuery, setDocSearchQuery] = useState("");

    // Fetch all knowledge base items (same as Library pages) to ensure folderId and isLocked are included
    const {
        allDocuments: kbItemsRaw,
    } = useMergedKnowledgeDocuments({ enabled: true });

    const kbItems = useMemo(() => {
        if (!Array.isArray(kbItemsRaw) || kbItemsRaw.length === 0) return kbItemsRaw ?? [];
        return kbItemsRaw.map((item: Record<string, unknown>) => {
            let meta: Record<string, unknown> = {};
            if (item.metadata) {
                if (typeof item.metadata === "object") meta = item.metadata as Record<string, unknown>;
                else if (typeof item.metadata === "string") {
                    try {
                        meta = JSON.parse(item.metadata as string) as Record<string, unknown>;
                    } catch {
                        meta = {};
                    }
                }
            }
            const fromMeta = meta.isLocked === true || meta.isLocked === "true" || !!(meta.lockedAt);
            return { ...item, isLocked: !!item.isLocked || !!item.lockedAt || fromMeta };
        });
    }, [kbItemsRaw]);

    // Embedding status for document actions menu (must be after kbItems is defined)
    const contentNoteIds = useMemo(() => (kbItems ?? []).map((i: { id: string }) => i.id), [kbItems]);
    const embeddingStatusQueryKey = useMemo(
        () => ["embedding-status-sidebar", contentNoteIds.slice().sort().join(",")],
        [contentNoteIds]
    );
    const {data: embeddingStatusMap = {}} = useQuery({
        queryKey: embeddingStatusQueryKey,
        queryFn: () => ragService.getEmbeddingStatusForDocuments(contentNoteIds),
        enabled: activeTab === "documents" && !!user && contentNoteIds.length > 0,
        staleTime: 60000,
    });

    /*const {data: kbItems = []} = useQuery({
        queryKey: ["/api/knowledge-base", user?.id],
        queryFn: async () => {
            if (!user) return [];
            return await knowledgeBaseService.getKnowledgeBaseItems();
        },
        enabled: !!user,
        staleTime: 30000,
        gcTime: 5 * 60 * 1000,
    });*/

    // Fetch folders for library tree — match Library page behavior:
    // - Personal (no workspace): getFolders() with no arg = all folders, same as Library
    // - Workspace selected: getFolders(workspaceId) = only that workspace's folders
    const folderWorkspaceParam = currentWorkspaceId ?? undefined;
    const {data: folders = []} = useQuery({
        queryKey: ["/api/library-folders", user?.id, folderWorkspaceParam === undefined ? "all" : folderWorkspaceParam ?? "personal"],
        queryFn: async () => {
            if (!user) return [];
            return await foldersService.getFolders(folderWorkspaceParam);
        },
        enabled: !!user,
        staleTime: 60000,
    });

    // Fetch spaces for library tree (workspace-scoped)
    const {data: librarySpaces = []} = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", currentWorkspaceId ?? null],
        queryFn: async () => mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null),
        enabled: !!user,
        staleTime: 60000,
    });

    const {data: pinStatus} = useQuery<{hasPinSet: boolean; isSessionValid: boolean}>({
        queryKey: ["pin-status"],
        queryFn: async () => {
            const {pinService} = await import("@/services/pinService");
            return pinService.getStatus();
        },
        enabled: !!user,
    });

    const renameMutation = useMutation({
        mutationFn: async ({id, title}: { id: string; title: string }) =>
            knowledgeBaseService.updateKnowledgeBaseItem(id, {title}),
        onSuccess: () => {
            toast({title: "Document renamed", description: "The document title has been updated."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            setRenameDialogOpen(false);
            setRenamingItem(null);
            setNewTitle("");
        },
        onError: (error) => {
            toast({title: "Error renaming", description: error instanceof Error ? error.message : "Failed to rename", variant: "destructive"});
        },
    });

    const duplicateMutation = useMutation({
        mutationFn: (id: string) => knowledgeBaseService.duplicateDocument(id),
        onSuccess: () => {
            toast({title: "Document duplicated", description: "A copy has been created."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
        },
        onError: (error) => {
            toast({title: "Error duplicating", description: error instanceof Error ? error.message : "Failed to duplicate", variant: "destructive"});
        },
    });

    const mergeMutation = useMutation({
        mutationFn: async ({sourceId, targetId}: { sourceId: string; targetId: string }) =>
            knowledgeBaseService.mergeDocuments(sourceId, targetId),
        onSuccess: () => {
            toast({title: "Documents merged", description: "The documents have been combined."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            setMergeDialogOpen(false);
            setMergeSourceItem(null);
            setMergeTargetId("");
        },
        onError: (error) => {
            toast({title: "Error merging", description: error instanceof Error ? error.message : "Failed to merge", variant: "destructive"});
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => knowledgeBaseService.deleteKnowledgeBaseItem(id),
        onSuccess: () => {
            toast({title: "Document deleted", description: "The document has been removed."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
        },
        onError: (error) => {
            toast({title: "Error deleting", description: error instanceof Error ? error.message : "Failed to delete", variant: "destructive"});
        },
    });

    const moveToFolderMutation = useMutation({
        mutationFn: async ({
            itemId,
            folderId,
            itemType,
        }: { itemId: string; folderId: string | null; itemType: "note" | "reference" }) => {
            if (itemType === "note") {
                return knowledgeBaseService.updateKnowledgeBaseItem(itemId, {folderId});
            }
            return sourcesService.updateSource(itemId, {folderId} as any);
        },
        onSuccess: () => {
            toast({title: "Item moved", description: "The item has been moved to the folder."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            queryClient.invalidateQueries({queryKey: ["/api/sources"]});
            queryClient.invalidateQueries({queryKey: ["/api/library-folders"]});
            setMoveToFolderDialogOpen(false);
            setMoveFolderItem(null);
            setMoveFolderTargetId(null);
        },
        onError: (error) => {
            toast({title: "Error moving item", description: error instanceof Error ? error.message : "Failed to move", variant: "destructive"});
        },
    });

    const noteItemsForMerge = useMemo(() => {
        const items = (kbItems ?? []) as { id: string; title?: string }[];
        return items.filter((item) => item.id !== mergeSourceItem?.id);
    }, [kbItems, mergeSourceItem?.id]);

    // Fetch reference sources
    /* const { data: sources = [] } = useQuery<any[]>({
       queryKey: ["/api/sources"],
     });*/

    const handleDocumentClick = useCallback(
        (documentId: string, isLocked?: boolean) => {
            if (isLocked && !pinStatus?.isSessionValid) {
                setPendingDocumentIdToOpen(documentId);
                setShowPinDialogForDocument(true);
                return;
            }
            // Check if we're currently saving a document
            if (autoSaveStatus === "saving" && editingItemId !== documentId) {
                // Store the pending document ID to switch to after save completes
                setPendingDocumentSwitch(documentId);
                // Show user-friendly message
                toast({
                    title: "Saving in progress",
                    description: "Please wait while the current document finishes saving...",
                    duration: 3000,
                });
                return;
            }
            // If save is complete or no document is being edited, switch immediately
            const doc = (kbItems ?? []).find((d: {id: string}) => d.id === documentId) as
                | {userId?: string; metadata?: unknown; spaceId?: string | null}
                | undefined;
            const linkedSpace = resolveLinkedSpaceIdForSharedEdit(doc, user?.id);
            if (linkedSpace) {
                setDocumentEditSpaceContextId(linkedSpace);
            }
            setIsCollapsed(false);
            setSelectedDocumentId(documentId);
            setActiveTab("create");
        },
        [
            setSelectedDocumentId,
            setDocumentEditSpaceContextId,
            setIsCollapsed,
            pinStatus?.isSessionValid,
            autoSaveStatus,
            editingItemId,
            toast,
            kbItems,
            user?.id,
        ]
    );

    const handleFolderClick = useCallback(
        (folderId: string) => {
            setSelectedFolderId(folderId);
            navigate("/library");
        },
        [navigate, setSelectedFolderId]
    );

    const handleRootClick = useCallback(() => {
        setSelectedFolderId(null);
        navigate("/library");
    }, [navigate, setSelectedFolderId]);

    const handleSpacesClick = useCallback(() => {
        setSelectedFolderId("spaces");
        navigate("/library");
    }, [navigate, setSelectedFolderId]);

    const handleSpaceClick = useCallback(
        (spaceId: string) => {
            setSelectedFolderId(`space-${spaceId}`);
            navigate("/library");
        },
        [navigate, setSelectedFolderId]
    );

    const documentActions = useMemo(() => ({
        onEdit: (item: DocumentActionsItem) => {
            if (item.itemType === "note") {
                if (item.isLocked && !pinStatus?.isSessionValid) {
                    setPendingDocumentIdToOpen(item.id);
                    setShowPinDialogForDocument(true);
                    return;
                }
                const doc = (kbItems ?? []).find((d: {id: string}) => d.id === item.id) as
                    | {userId?: string; metadata?: unknown; spaceId?: string | null}
                    | undefined;
                const linkedSpace = resolveLinkedSpaceIdForSharedEdit(doc, user?.id);
                if (linkedSpace) {
                    setDocumentEditSpaceContextId(linkedSpace);
                }
                setSelectedDocumentId(item.id);
                setActiveTab("create");
            }
        },
        onRename: (item: DocumentActionsItem) => {
            setRenamingItem(item);
            setNewTitle(item.title ?? "");
            setRenameDialogOpen(true);
        },
        onDuplicate: (item: DocumentActionsItem) => {
            if (item.itemType === "note") duplicateMutation.mutate(item.id);
        },
        onMerge: (item: DocumentActionsItem) => {
            setMergeSourceItem(item);
            setMergeTargetId("");
            setMergeDialogOpen(true);
        },
        onManageLinks: (item: DocumentActionsItem) => {
            setInspectorItem(item);
            setInspectorOpen(true);
        },
        onReembedForSearch: async (item: DocumentActionsItem) => {
            try {
                await ragService.reembedDocument(item.id);
                toast({title: "Re-embedded for search", description: `"${item.title}" has been updated for semantic search.`});
                queryClient.invalidateQueries({queryKey: ["embedding-status-sidebar"]});
            } catch (e) {
                toast({title: "Re-embed failed", description: e instanceof Error ? e.message : "Could not re-embed document.", variant: "destructive"});
            }
        },
        onEmbedForSearch: async (item: DocumentActionsItem) => {
            try {
                await ragService.embedDocument(item.id);
                toast({title: "Embedded for search", description: `"${item.title}" is now searchable by meaning.`});
                queryClient.invalidateQueries({queryKey: ["embedding-status-sidebar"]});
            } catch (e) {
                toast({title: "Embed failed", description: e instanceof Error ? e.message : "Could not embed document.", variant: "destructive"});
            }
        },
        onRemoveFromSearch: async (item: DocumentActionsItem) => {
            try {
                await ragService.deleteDocumentEmbeddings(item.id);
                toast({title: "Removed from search", description: `"${item.title}" is no longer embedded.`});
                queryClient.invalidateQueries({queryKey: ["embedding-status-sidebar"]});
            } catch (e) {
                toast({title: "Remove failed", description: e instanceof Error ? e.message : "Could not remove embeddings.", variant: "destructive"});
            }
        },
        onToggleIncludeInRag: async (item: DocumentActionsItem, value: boolean) => {
            try {
                await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {includeInRag: value});
                toast({title: value ? "Included in RAG" : "Excluded from RAG", description: `"${item.title}" is ${value ? "in" : "out of"} RAG retrieval.`});
                queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            } catch (e) {
                toast({title: "Update failed", description: e instanceof Error ? e.message : "Could not update.", variant: "destructive"});
            }
        },
        onMoveToFolder: (item: DocumentActionsItem) => {
            setMoveFolderItem(item);
            setMoveFolderTargetId(null);
            setMoveToFolderDialogOpen(true);
        },
        onRemoveFromFolder: (item: DocumentActionsItem) => {
            moveToFolderMutation.mutate({itemId: item.id, folderId: null, itemType: item.itemType});
        },
        onDelete: (item: DocumentActionsItem) => {
            if (item.itemType !== "note") return;
            if (confirm(`Are you sure you want to delete "${item.title}"?`)) {
                deleteMutation.mutate(item.id);
            }
        },
        onOpenLink: (item: DocumentActionsItem) => {
            if (item.url) window.open(item.url, "_blank");
        },
        onViewWorks: (item: DocumentActionsItem) => {
            navigate("/library", {replace: false});
        },
        onToggleSourceOfTruth: async (item: DocumentActionsItem) => {
            try {
                const added = await toggleDocumentSourceOfTruth(item.id);
                toast({
                    title: added ? "Source of Truth" : "Removed from canon",
                    description: added
                        ? `"${item.title ?? "Document"}" is always included in chat context.`
                        : `"${item.title ?? "Document"}" is no longer in your global canon.`,
                });
                queryClient.invalidateQueries({ queryKey: SOURCE_OF_TRUTH_QUERY_KEY });
                queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary", user?.id] });
            } catch (e) {
                toast({
                    title: "Could not update",
                    description: e instanceof Error ? e.message : "Unknown error",
                    variant: "destructive",
                });
            }
        },
        onCopyPublicLink: (item: DocumentActionsItem) => {
            if (item.itemType === "note") void copyPublicLink(item.id);
        },
        showCopyPublicLink: true,
    }), [
        pinStatus?.isSessionValid,
        copyPublicLink,
        setSelectedDocumentId,
        setDocumentEditSpaceContextId,
        kbItems,
        duplicateMutation,
        mergeMutation,
        moveToFolderMutation,
        deleteMutation,
        toast,
        queryClient,
        user?.id,
        navigate,
    ]);

    const handleConfirmRename = () => {
        if (renamingItem && newTitle.trim()) {
            renameMutation.mutate({id: renamingItem.id, title: newTitle.trim()});
        }
    };

    const handleConfirmMerge = () => {
        if (mergeSourceItem && mergeTargetId) {
            mergeMutation.mutate({sourceId: mergeSourceItem.id, targetId: mergeTargetId});
        }
    };

    const handleConfirmMoveToFolder = () => {
        if (moveFolderItem) {
            moveToFolderMutation.mutate({
                itemId: moveFolderItem.id,
                folderId: moveFolderTargetId,
                itemType: moveFolderItem.itemType,
            });
        }
    };

    const inspectorDrawerItem = useMemo(() => {
        if (!inspectorItem) return null;
        return {
            id: inspectorItem.id,
            title: inspectorItem.title ?? "",
            itemType: inspectorItem.itemType,
            folderId: inspectorItem.folderId ?? null,
            linkedSpaceIds: [] as string[],
            linkedTopicIds: [] as string[],
            linkedThreadIds: [] as string[],
            tags: (inspectorItem.tags ?? []) as string[],
        };
    }, [inspectorItem]);

    const handleInspectorRelationshipsUpdated = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary", user?.id] });
        queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
        queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
    }, [queryClient, user?.id]);

    return (
        <AttachmentPanel onOpenDocument={openDocumentHandler ?? undefined}>
            <RightSidebarAttachmentInner
                autoSaveStatus={autoSaveStatus}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                editingItemId={editingItemId}
                handleActivateCreateTab={handleActivateCreateTab}
                setAutoSaveStatus={setAutoSaveStatus}
                setEditingItemId={setEditingItemId}
                setOpenDocumentHandler={setOpenDocumentHandler}
                                    docSearchQuery={docSearchQuery}
                setDocSearchQuery={setDocSearchQuery}
                                    folders={folders}
                                    kbItems={kbItems}
                                    librarySpaces={librarySpaces}
                                    expandedFolderIds={expandedFolderIds}
                                    selectedFolderId={selectedFolderId}
                toggleFolderExpanded={toggleFolderExpanded}
                handleDocumentClick={handleDocumentClick}
                handleFolderClick={handleFolderClick}
                handleRootClick={handleRootClick}
                handleSpacesClick={handleSpacesClick}
                handleSpaceClick={handleSpaceClick}
                                    embeddingStatusMap={embeddingStatusMap}
                documentActions={documentActions as LibraryDocumentActions}
                showPinDialogForDocument={showPinDialogForDocument}
                setShowPinDialogForDocument={setShowPinDialogForDocument}
                setPendingDocumentIdToOpen={setPendingDocumentIdToOpen}
                pendingDocumentIdToOpen={pendingDocumentIdToOpen}
                setSelectedDocumentId={setSelectedDocumentId}
                renameDialogOpen={renameDialogOpen}
                setRenameDialogOpen={setRenameDialogOpen}
                newTitle={newTitle}
                setNewTitle={setNewTitle}
                handleConfirmRename={handleConfirmRename}
                renameIsPending={renameMutation.isPending}
                mergeDialogOpen={mergeDialogOpen}
                setMergeDialogOpen={setMergeDialogOpen}
                mergeSourceItem={mergeSourceItem}
                mergeTargetId={mergeTargetId}
                setMergeTargetId={setMergeTargetId}
                noteItemsForMerge={noteItemsForMerge}
                handleConfirmMerge={handleConfirmMerge}
                mergeIsPending={mergeMutation.isPending}
                moveToFolderDialogOpen={moveToFolderDialogOpen}
                setMoveToFolderDialogOpen={setMoveToFolderDialogOpen}
                moveFolderItem={moveFolderItem}
                moveFolderTargetId={moveFolderTargetId}
                setMoveFolderTargetId={setMoveFolderTargetId}
                handleConfirmMoveToFolder={handleConfirmMoveToFolder}
                moveToFolderIsPending={moveToFolderMutation.isPending}
                inspectorDrawerItem={inspectorDrawerItem}
                inspectorOpen={inspectorOpen}
                setInspectorOpen={setInspectorOpen}
                onInspectorRelationshipsUpdated={handleInspectorRelationshipsUpdated}
            />
        </AttachmentPanel>
    );
}
